import ViewportProviderInterface from './Providers/ViewportProviderInterface.js';
import AecModelDataViewportProvider from './Providers/AecModelDataViewportProvider.js';
import AlignmentServiceViewportProvider from './Providers/AlignmentServiceViewportProvider.js';
import AlignmentServiceViewport from './ViewportTypes/AlignmentServiceViewport.js';
import AecModelDataViewport from './ViewportTypes/AecModelDataViewport.js';
import { DEFAULT_VIEW_OFFSETS } from './ViewportTypes/ViewportInterface.js';
import { zOffsetHack } from '../levels/LevelUtils.js';
import Level from '../levels/Level.js';
import { locales } from './ViewportsLocales.js';

const av = Autodesk.Viewing;
const namespace = AutodeskNamespace('Autodesk.AEC');
const extensionName = 'Autodesk.AEC.ViewportsExtension';

const MAX_INT_32 = 2147483647;
/**
 * Viewports Extension is a centralized place that manages all of the viewports associated with models.
 * By default, viewports from AecModelData will be fetched and cached.
 * Any other viewport providers can be added (Alignment Service for example).
 *
 * @example
 *   viewer.loadExtension('Autodesk.AEC.ViewportsExtension', options)
 *
 * @memberof Autodesk.Viewing.Extensions
 * @see {@link Extension} for common inherited methods.
 * @class
 * @hideConstructor
 */
export class ViewportsExtension extends av.Extension {

    constructor(viewer, options) {
        super(viewer, options);

        av.EventDispatcher.prototype.apply(this);

        this.name = 'Viewports';

        this.onModelAdded = this.onModelAdded.bind(this);
        this.onViewportDataCreated = this.onViewportDataCreated.bind(this);
    }

    load() {
        super.load();

        this.extendLocalization(locales);

        this.cache = this.getCache();

        if (!this.cache.viewports) {
            this.cache.viewports = {};
        }

        this.providers = {};

        // Load AecModelDataViewportProvider by default.
        this.addViewportProvider(new AecModelDataViewportProvider());

        this.addEventListeners();

        this.fetchViewportsForAllModels();

        return true;
    }

    unload() {
        super.unload();

        this.providers = null;

        this.removeEventListeners();
        return true;
    }

    addViewportProvider(provider) {
        const name = provider.constructor.getProviderName();
        if (!this.providers[name]) {
            this.providers[name] = provider;
        }

        // Update viewports for all existing models.
        // Don't worry, if viewports already fetched for a given model & provider - it will be skipped.
        this.fetchViewportsForAllModels();
    }

    removeViewportProvider(provider) {
        delete this.providers[provider.constructor.getProviderName()];
    }

    fetchViewportsForAllModels() {
        const models = this.viewer.getAllModels();
        models.forEach(model => this.fetchViewports(model.getDocumentNode()));
    }

    addEventListeners() {
        this.viewer.addEventListener(av.MODEL_ADDED_EVENT, this.onModelAdded);
        this.addEventListener(ViewportsExtension.Events.VIEWPORT_DATA_CREATED_EVENT, this.onViewportDataCreated);
    }

    removeEventListeners() {
        this.viewer.removeEventListener(av.MODEL_ADDED_EVENT, this.onModelAdded);
        this.removeEventListener(ViewportsExtension.Events.VIEWPORT_DATA_CREATED_EVENT, this.onViewportDataCreated);
    }

    onModelAdded({ model }) {
        this.fetchViewports(model.getDocumentNode());
    }

    // Generate artificial levels based on viewports.
    async generateLevelsFromViewports(bubbleNode) {
        const viewports = await this.getViewportsAsync(bubbleNode);

        if (!viewports) {
            return [];
        }

        const levels = [];
        const LevelHeightThreshold = 1;

        viewports.sort((v1, v2) => v1.getElevation() - v2.getElevation());

        for (let i = 0; i < viewports.length - 1; i++) {
            // Don't generate more than one level per height.
            // In case two viewports have about the same height, create a single level based on the first one.
            if (Math.abs(viewports[i].getElevation() - viewports[i + 1].getElevation()) < LevelHeightThreshold) {
                viewports.splice(i + 1, 1);
                i--;
            }
        }

        for (let i = 0; i < viewports.length; i++) {
            const currentElevation = viewports[i].getElevation();
            let nextElevation = undefined;

            // Topmost level - we don't know its height, so we'll assume it's REALLY tall.
            if (i === viewports.length - 1) {
                nextElevation = MAX_INT_32; // Equivalent to the top level values in AecModelData levels.
            } else {
                // For every other level, calculate its height according to the next level on top of it.
                nextElevation = viewports[i + 1].getElevation();
            }

            levels.push(
                new Level(
                    i,
                    i.toString(),
                    `${Autodesk.Viewing.i18n.translate('Level')} #${i + 1}`,
                    currentElevation - zOffsetHack,
                    nextElevation,
                    true,
                )
            );
        }

        return levels;
    }

    onViewportDataCreated({ models }) {
        models.forEach(model => {
            // Invalidate cache and refetch the viewports data
            const key = model.getModelKey();
            if (this.cache.viewports[key]) {
                delete this.cache.viewports[key];

                // Refetch viewports.
                this.onModelAdded({ model });
            }
        });
    }

    // Fetch and get viewports for a given bubble.
    // In case that the viewports were previously requested - don't worry, you'll get them from cache.
    async getViewportsAsync(bubbleNode, filterOptions) {
        await this.fetchViewports(bubbleNode);
        return this.filterAndFlattenViewports(bubbleNode, filterOptions);
    }

    // Returns only from what's already cached
    getViewports(bubbleNode, filterOptions) {
        return this.filterAndFlattenViewports(bubbleNode, filterOptions);
    }

    // Filter viewports according to given flags, and organize them in a flat array.
    // filterOptions:
    //          - filter2DTo3DTransformSupport: filter only viewports that support 2D to 3D transform.
    //          - onlyTopViewPlans: filter only top view viewports.
    //          - filterCB: additional filter callback.
    filterAndFlattenViewports(bubbleNode, filterOptions = {}) {
        const filter2DTo3DTransformSupport = filterOptions.filter2DTo3DTransformSupport ?? true;
        const onlyTopViewPlans = filterOptions.onlyTopViewPlans ?? true;
        const filterCB = filterOptions.filterCB;

        const key = bubbleNode.getModelKey();
        const viewportsPerProvider = this.cache.viewports[key];

        if (!viewportsPerProvider) {
            return [];
        }

        let viewports = Object.values(viewportsPerProvider).flat();

        if (filter2DTo3DTransformSupport) {
            viewports = viewports.filter(viewport => viewport.supports2DTo3DTransform());
        }

        if (onlyTopViewPlans) {
            viewports = viewports.filter(viewport => viewport.isTopViewViewport());
        }

        if (filterCB) {
            viewports = viewports.filter(filterCB);
        }

        return viewports;
    }

    // Fetch viewports for a given bubbleNode.
    async fetchViewports(bubbleNode) {
        if (!bubbleNode) {
            return;
        }

        const key = bubbleNode.getModelKey();
        if (!this.cache.viewports[key]) {
            this.cache.viewports[key] = {};
        }

        // Wait for all providers to finish before returning from function,
        // but run each provider in parallel so nothing is blocked.
        await Promise.all(Object.values(this.providers).map(async provider => {
            const providerName = provider.constructor.getProviderName();

            // Fetch viewports only if they are not in cache yet.
            if (!this.cache.viewports[key][providerName]) {
                // Add a placeholder in cache so it won't fetch viewports multiple times for the
                // same provider (in case of multiple calls to fetchViewports)
                this.cache.viewports[key][providerName] = [];

                // Save the current promise to be returned on subsequent fetchViewports calls,
                // so that we don't return those calls until data is ready
                this.cache.viewports[key][providerName].pendingPromise = provider.fetchViewports(bubbleNode);
                const viewports = await this.cache.viewports[key][providerName].pendingPromise;

                delete this.cache.viewports[key][providerName].pendingPromise;

                if (viewports.length) {
                    this.cache.viewports[key][providerName] = viewports;
                    this.fireEvent({
                        type: ViewportsExtension.Events.VIEWPORT_DATA_FETCHED_EVENT,
                        modelKey: key,
                        providerName,
                        viewports
                    });
                }
            } else if (this.cache.viewports[key][providerName].pendingPromise) {
                await this.cache.viewports[key][providerName].pendingPromise;
            }
        }));
    }

    // Given a viewport array, a sheet and a point on that sheet, this function returns the viewport containing it.
    // If multiple viewports overlap, we choose the one where (x,y) is "most inside", i.e.,
    // where the distance from the viewport edges is largest.
    //
    //  @param {av.Model}         sheet
    //  @param {Vector2}          point            - Point in sheet world-coords.
    //  @param {Object}           filterOptions
    //
    //  @returns {Viewport|null}
    findViewportAtPoint(sheet, point, filterOptions) {
        let viewports = this.filterAndFlattenViewports(sheet.getDocumentNode(), filterOptions);

        // Couple each viewport with viewport bounds
        viewports = viewports.map(vp => {
            return {
                viewport: vp,
                bounds: vp.getViewportBounds(sheet.getUnitScale())
            };
        });

        // Reduce to viewports containing the given point
        const containsPoint = vp => vp.bounds?.containsPoint(point);

        viewports = viewports.filter(containsPoint);

        // Point may be outside of all viewports
        if (!viewports.length) {
            return null;
        }

        // Viewports may overlap. For this case, we need a heuristic metric
        // to choose the best match. For this, we use the
        // distance to the viewport boundary edges.
        const distanceFromEdge = (box, p) => {
            return Math.min(
                Math.min(p.x - box.min.x, box.max.x - p.x),
                Math.min(p.y - box.min.y, box.max.y - p.y)
            );
        };

        // Pick the viewport that minimizes distanceFromEdge
        let getBestMatch = (vp1, vp2) => {
            let d1 = distanceFromEdge(vp1.bounds, point);
            let d2 = distanceFromEdge(vp2.bounds, point);
            return d2 > d1 ? vp2 : vp1;
        };

        return viewports.reduce(getBestMatch).viewport;
    }

    // Given a level guid, find all viewports that are contained in this level.
    //
    //  @param {av.Model}         model3D          - A 3D model that contains levels & viewports.
    //  @param {String}           levelGuid        - Level guid
    //
    //  @returns {Array<Viewport>}
    findViewportsInLevel(model3D, levelGuid) {
        const levelExt = this.viewer.getExtension('Autodesk.AEC.LevelsExtension');

        if (!levelExt) {
            return [];
        }

        const levels = levelExt.floorSelector.floorData;
        const level = levels.find(level => level.guid === levelGuid);

        // No matching level for the given level guid.
        if (!level) {
            return [];
        }

        const viewports = this.getViewports(model3D.getDocumentNode(), {
            filterCB: viewport => viewport.isViewportInLevel(level)
        });

        return viewports;
    }

    // Returns true if deleted successfully
    async deleteViewport(vp, key) {
        const type = this.getViewportType(vp);

        // Update cache and fire event with updated viewports so we see the result immediately
        const viewports = this.cache.viewports[key]?.[type];
        if (!viewports) {
            return false;
        }

        const idx = viewports.findIndex(_vp => _vp.getGuid() === vp.getGuid());
        if (idx === -1) {
            return false;
        }

        viewports.splice(idx, 1);
        this.fireEvent({ type: ViewportsExtension.Events.VIEWPORT_DATA_FETCHED_EVENT, modelKey: key, providerName: type, viewports });

        // Now delete in the provider (if allowed)
        const success = await this.providers[type]?.deleteViewport(vp);
        if (!success) {
            console.warn('Could not delete viewport or not allowed.', vp);

            // Restore viewport to cache
            viewports.splice(idx, 0, vp);
            this.fireEvent({ type: ViewportsExtension.Events.VIEWPORT_DATA_FETCHED_EVENT, modelKey: key, providerName: type, viewports });
            return false;
        }

        return true;
    }

    getViewportType(vp) {
        if (vp instanceof AecModelDataViewport) {
            return AecModelDataViewportProvider.getProviderName();
        } else if (vp instanceof AlignmentServiceViewport) {
            return AlignmentServiceViewportProvider.getProviderName();
        }
    }

    findViewportByGuid(guid) {
        for (const key in this.cache.viewports) {
            const viewportsPerProvider = this.cache.viewports[key];
            const viewports = Object.values(viewportsPerProvider).flat();
            const vp = viewports.find(vp => vp.getGuid() === guid);

            if (vp) {
                return vp;
            }
        }

        return null;
    }
}

namespace.ViewportsExtension = ViewportsExtension;

// Events
ViewportsExtension.Events = {
    VIEWPORT_DATA_FETCHED_EVENT: 'viewportDataFetched',
    VIEWPORT_DATA_CREATED_EVENT: 'viewportDataCreated',
};

// Exports
ViewportsExtension.ViewportProviderInterface = ViewportProviderInterface;
ViewportsExtension.AecModelDataViewportProvider = AecModelDataViewportProvider;
ViewportsExtension.AlignmentServiceViewportProvider = AlignmentServiceViewportProvider;
ViewportsExtension.AlignmentServiceViewport = AlignmentServiceViewport;
ViewportsExtension.AecModelDataViewport = AecModelDataViewport;

av.theExtensionManager.registerExtension(extensionName, ViewportsExtension);
